import { styles } from './apiKeys.jcss'
import translate from '../../../../core/translate'
import Modal from '../../../../blocks.simple/modal/modal'
import React, { useEffect, useState } from 'react'
import Button from '../../../../blocks.simple/button/button'
import Checkbox from '../../../../blocks.simple/checkbox/checkbox'
import Icon from '../../../../blocks.simple/icon/icon'
import { api } from '../../../../core/api/ConnectionManager'
import EditText from '../../../../blocks.simple/editText/editText'
import moment from 'moment/moment'
import Toast from '../../../../blocks.simple/toast/toast'

type ApiKey = {
    createdAt: string
    id: number
    userId: number
    apiKey: string
    title: string
    expiredAt: string
    lastUsage: string | null
}

export const ApiKeys = ({ opened, onClose }: any) => {
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
    const [selectedItems, setSelectedItems] = useState<number[]>([])
    const [createApiKey, setCreateApiKey] = useState<boolean>(false)
    const [apiKeyName, setApiKeyName] = useState<string>('')
    const [newApiKeyData, setNewApiKeyData] = useState<ApiKey | null>(null)
    const [apiKeyEndDate, setApiKeyEndDate] = useState<string>('')
    const [iHaveKeyCopy, setIHaveKeyCopy] = useState<boolean>(false)
    const [confirmDeleteItems, setConfirmDeleteItems] = useState<number[] | null>(null)

    const onSelectAll = (value: boolean) => {
        if (value) {
            setSelectedItems(apiKeys.map((apiKey) => apiKey.id))

            return
        }

        setSelectedItems([])
    }

    const createNewApiKey = () => {
        const endDate = moment(apiKeyEndDate).toISOString()

        api.send('createApiKey', {
            title: apiKeyName,
            expiredAt: endDate,
        }).then((res: any) => {
            if (!res) return

            setNewApiKeyData(res)
        })
    }

    const deleteApiKeys = () => {
        const deleteAll = confirmDeleteItems && !confirmDeleteItems.length
        setSelectedItems([])

        api.send(
            deleteAll ? 'deleteAllApiKeys' : 'deleteApiKeys',
            deleteAll ? {} : { apiKeysId: confirmDeleteItems }
        ).then(() => {
            setConfirmDeleteItems(null)
        })
    }

    const onCloseCreateApiKey = () => {
        setApiKeyName('')
        setCreateApiKey(false)
        setNewApiKeyData(null)
        setIHaveKeyCopy(false)
    }

    const copyToClipboard = (data: string) => {
        const clipboardResultToastOptions = {
            autoClose: 3000,
            position: 'bottom-left',
        }

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(data).then(() => {
                Toast.info(translate('copiedToClipboard'), clipboardResultToastOptions)
            })
        }
    }

    useEffect(() => {
        api.send('getApiKeys', {}).then((res: any) => {
            if (!res) return

            setApiKeys(res)
        })
    }, [])

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver(
            'apiKeyCreated',
            (apiKey: ApiKey) => {
                setTimeout(() => {
                    setApiKeys([apiKey, ...apiKeys])
                }, 100)
            },
            listenersId
        )
        api.addObserver(
            'apiKeyUpdated',
            (apiKey: ApiKey) => {
                setApiKeys(
                    apiKeys.map((item) => {
                        if (item.id === apiKey.id) {
                            return apiKey
                        }

                        return item
                    })
                )
            },
            listenersId
        )
        api.addObserver(
            'apiKeyDeleted',
            (data: { id: number[] }) => {
                setApiKeys(apiKeys.filter((item) => !data.id.includes(item.id)))
            },
            listenersId
        )

        return () => {
            listenersId.forEach((id) => api.removeObserver(id))
        }
    }, [apiKeys])

    return (
        <Modal open={opened} useBackButton={true} onClose={onClose}>
            <div className={styles.modalWrapper}>
                <div className={styles.header}>
                    <div className={styles.title}>{translate('apiKeysAccess')}</div>
                    <div className={styles.headerButtons}>
                        <Button mod={'withBorder'} onClick={() => setConfirmDeleteItems([])}>
                            {translate('deleteAllKeys')}
                        </Button>
                        <Button mod={'fill'} className={styles.createKeyButton} onClick={() => setCreateApiKey(true)}>
                            {translate('createKey')}
                        </Button>
                    </div>
                </div>
                <div className={styles.apiKeysTableWrapper}>
                    <table>
                        <tr className={styles.headerRow}>
                            <td style={{ width: 65 }}>
                                <Checkbox
                                    checked={!!selectedItems.length}
                                    onClick={(value: boolean) => onSelectAll(value)}
                                />
                            </td>
                            <td style={{ width: '30%' }}>{translate('keyName')}</td>
                            <td style={{ width: '20%' }}>{translate('key')}</td>
                            <td style={{ width: '16%' }}>{translate('creationDate')}</td>
                            <td style={{ width: '16%' }}>{translate('endDate')}</td>
                            <td style={{ width: '22%' }}>{translate('lastUsage')}</td>
                            <td style={{ width: 56 }}>
                                <Icon
                                    type={'delete'}
                                    disabled={!selectedItems.length}
                                    onClick={() => setConfirmDeleteItems(selectedItems)}
                                    tooltip={{
                                        title: translate('deleteSelected'),
                                    }}
                                />
                            </td>
                        </tr>
                        {apiKeys.map((apiKey) => (
                            <tr>
                                <td>
                                    <Checkbox
                                        checked={selectedItems.includes(apiKey.id)}
                                        onClick={(value: boolean) => {
                                            if (value) {
                                                setSelectedItems([...selectedItems, apiKey.id])

                                                return
                                            }

                                            setSelectedItems(selectedItems.filter((item) => item !== apiKey.id))
                                        }}
                                    />
                                </td>
                                <td className={styles.keyName} style={{ wordWrap: 'break-word' }}>
                                    {apiKey.title}
                                </td>
                                <td>****{apiKey.apiKey}</td>
                                <td>{moment(apiKey.createdAt).format('DD.MM.YYYY')}</td>
                                <td>{moment(apiKey.expiredAt).format('DD.MM.YYYY')}</td>
                                <td>{apiKey.lastUsage ? moment(apiKey.lastUsage).format('DD.MM.YYYY') : '—'}</td>
                                <td className={styles.delete} onClick={() => setConfirmDeleteItems([apiKey.id])}>
                                    <Icon
                                        type={'delete'}
                                        tooltip={{
                                            title: translate('delete'),
                                        }}
                                    />
                                </td>
                            </tr>
                        ))}
                    </table>
                </div>
            </div>
            {createApiKey && (
                <div className={styles.createApiKey}>
                    <div className={styles.createApiKeyInner}>
                        <div className={styles.caption}>{translate('apiKeyCreation')}</div>
                        {!newApiKeyData ? (
                            <>
                                <div className={styles.text}>{translate('apiKeyInfo_1')}</div>
                                <div className={styles.text}>{translate('apiKeyInfo_2')}</div>
                                <div className={styles.text}>{translate('apiKeyInfo_3')}</div>
                                <EditText
                                    containerClass={styles.field}
                                    value={apiKeyName}
                                    onChange={(value: string) => {
                                        setApiKeyName(value)
                                    }}
                                    label={translate('createKeyName')}
                                />
                                <EditText
                                    type={'date'}
                                    containerClass={styles.field}
                                    value={moment(apiKeyEndDate).format('DD.MM.YYYY')}
                                    label={translate('endDate')}
                                    onChange={(value: string) => {
                                        setApiKeyEndDate(value)
                                    }}
                                    minDate={moment().toDate()}
                                    maxDate={moment().add(12, 'M').toDate()}
                                />
                                <div className={styles.buttons}>
                                    <Button
                                        mod={'withBorder'}
                                        className={styles.button}
                                        textSize={'mini'}
                                        indentSize={'mini'}
                                        rounded={'full_normal'}
                                        onClick={onCloseCreateApiKey}
                                    >
                                        {translate('cancel')}
                                    </Button>
                                    <Button
                                        mod={'fill'}
                                        className={styles.button}
                                        textSize={'mini'}
                                        indentSize={'mini'}
                                        rounded={'full_normal'}
                                        onClick={createNewApiKey}
                                    >
                                        {translate('create')}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.fieldWithIcon}>
                                    <EditText
                                        containerClass={styles.field}
                                        className={styles.inputClassName}
                                        value={newApiKeyData.title}
                                        label={translate('keyName')}
                                    />
                                    <Icon
                                        type={'copyToClipboard'}
                                        size={18}
                                        containerClass={styles.copyIcon}
                                        onClick={() => copyToClipboard(newApiKeyData.title)}
                                        tooltip={{
                                            title: translate('copyToClipboard'),
                                        }}
                                    />
                                </div>
                                <div className={styles.fieldWithIcon}>
                                    <EditText
                                        containerClass={styles.field}
                                        className={styles.inputClassName}
                                        value={newApiKeyData.apiKey}
                                        label={translate('apiKey')}
                                    />
                                    <Icon
                                        type={'copyToClipboard'}
                                        size={18}
                                        containerClass={styles.copyIcon}
                                        onClick={() => copyToClipboard(newApiKeyData.apiKey)}
                                        tooltip={{
                                            title: translate('copyToClipboard'),
                                        }}
                                    />
                                </div>
                                <div className={styles.fieldWithIcon}>
                                    <EditText
                                        containerClass={styles.field}
                                        className={styles.inputClassName}
                                        value={moment(newApiKeyData.expiredAt).format('DD.MM.YYYY')}
                                        label={translate('endDate')}
                                    />
                                    <Icon
                                        type={'copyToClipboard'}
                                        size={18}
                                        containerClass={styles.copyIcon}
                                        onClick={() =>
                                            copyToClipboard(moment(newApiKeyData.expiredAt).format('DD.MM.YYYY'))
                                        }
                                        tooltip={{
                                            title: translate('copyToClipboard'),
                                        }}
                                    />
                                </div>
                                <div className={styles.text} style={{ marginTop: 20 }}>
                                    {translate('apiKeyInfo_4')}
                                </div>
                                <div className={styles.text}>{translate('apiKeyInfo_5')}</div>
                                <div>
                                    <Checkbox
                                        onClick={setIHaveKeyCopy}
                                        checked={iHaveKeyCopy}
                                        label={translate('iHaveKeyCopy')}
                                        labelClassName={styles.checkboxLabel}
                                        wrapperClassName={styles.checkboxWrapper}
                                    />
                                </div>
                                <div className={styles.buttons}>
                                    <Button
                                        mod={'fill'}
                                        className={styles.button}
                                        textSize={'mini'}
                                        indentSize={'mini'}
                                        rounded={'full_normal'}
                                        onClick={onCloseCreateApiKey}
                                        disabled={!iHaveKeyCopy}
                                    >
                                        {translate('close')}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {confirmDeleteItems && (
                <div className={styles.createApiKey}>
                    <div className={styles.createApiKeyInner}>
                        <div>
                            {confirmDeleteItems && !confirmDeleteItems.length && translate('confirmDeleteAllKeys')}
                            {confirmDeleteItems && confirmDeleteItems.length > 1 && translate('confirmDeleteKeys')}
                            {confirmDeleteItems && confirmDeleteItems.length === 1 && translate('confirmDeleteKey')}
                        </div>
                        <div className={styles.buttons}>
                            <Button
                                mod={'withBorder'}
                                className={styles.button}
                                textSize={'mini'}
                                indentSize={'mini'}
                                rounded={'full_normal'}
                                onClick={() => setConfirmDeleteItems(null)}
                            >
                                {translate('cancel')}
                            </Button>
                            <Button
                                mod={'fill'}
                                className={styles.button}
                                textSize={'mini'}
                                indentSize={'mini'}
                                rounded={'full_normal'}
                                onClick={deleteApiKeys}
                            >
                                {translate('delete')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    )
}
